import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FaTimes,
  FaClipboardCheck,
  FaUser,
  FaPaperPlane,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
} from 'react-icons/fa';

const normalizeFormType = (formType) => {
  if (formType === '1120-S') return '1120S';
  return formType;
};

const ChecklistModal = ({ show, onHide, userId, memberIndex = -1, formType }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [commentOpen, setCommentOpen] = useState({});

  const modalRoot = typeof document !== 'undefined' ? document.body : null;
  const resolvedFormType = normalizeFormType(formType);
  const resolvedMemberIndex = memberIndex >= 0 ? memberIndex : -1;

  const fetchChecklist = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('employeeToken');
      const query = new URLSearchParams({
        userId,
        memberIndex: String(resolvedMemberIndex),
        formType: resolvedFormType,
        type: 'prepare',
      });

      let response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 404) {
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            memberIndex: resolvedMemberIndex,
            formType: resolvedFormType,
            type: 'prepare',
          }),
        });

        if (response.status === 400) {
          response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists?${query}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }

      if (!response.ok) {
        throw new Error('Failed to fetch or create checklist');
      }

      const data = await response.json();
      setChecklist(data.checklist);
      const initialCommentOpen = {};
      (data.checklist?.items || []).forEach((item, idx) => {
        if (item.remarks && item.remarks.trim()) {
          initialCommentOpen[idx] = true;
        }
      });
      setCommentOpen(initialCommentOpen);
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError(err.message || 'Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && userId) {
      fetchChecklist();
    }
  }, [show, userId, memberIndex, formType]);

  const renderModalShell = (children) => {
    if (!modalRoot) return null;
    return createPortal(
      <div className="fixed inset-0 z-[1300] flex items-center justify-center p-3 sm:p-6" onClick={onHide}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-4xl lg:max-w-5xl min-h-[72vh] max-h-[92vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>,
      modalRoot
    );
  };

  // Safety check to prevent null userId errors
  if (show && !userId) {
    return renderModalShell(
      <>
        <div className="p-4 bg-gradient-to-r from-red-500 to-rose-600 text-white border-0 flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FaExclamationTriangle /> Checklist Error
          </h2>
          <button type="button" onClick={onHide} className="p-2 hover:bg-white/20 rounded-lg">
            <FaTimes />
          </button>
        </div>
        <div className="p-6 text-center">
          <div className="inline-flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl text-left">
            <FaExclamationTriangle className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold mb-1">Cannot Load Checklist</h5>
              <p className="text-sm mb-0">Missing required user information. Please try again or contact support.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!show) return null;

  const handleResponseChange = (index, value) => {
    const updatedChecklist = { ...checklist };
    updatedChecklist.items[index].response = value;
    setChecklist(updatedChecklist);
    updateChecklistItem(index, value, updatedChecklist.items[index].remarks);
  };

  const handleRemarksChange = (index, value) => {
    const updatedChecklist = { ...checklist };
    updatedChecklist.items[index].remarks = value;
    setChecklist(updatedChecklist);
  };

  const updateChecklistItem = async (index, response, remarks) => {
    try {
      const token = localStorage.getItem('employeeToken');
      
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/update-item`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          itemIndex: index,
          response,
          remarks
        })
      });
    } catch (err) {
      console.error('Error updating checklist item:', err);
    }
  };

  const handleSubmitChecklist = async () => {
    if (!checklist) return;
    
    // Check if all items have responses (NA is a valid response)
    const hasIncompleteItems = checklist.items.some(item => 
      item.response === undefined || item.response === null || item.response === ''
    );
    
    if (hasIncompleteItems) {
      setError('Please complete all checklist items before submitting');
      return;
    }

    // Removed NA remarks validation as per user request
    
    setSubmitting(true);
    setError('');
    
    try {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setSubmitting(false);
        return;
      }
      
      // Fetch employee information from the backend using the token
      const employeeResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!employeeResponse.ok) {
        throw new Error('Failed to fetch employee information');
      }
      
      const employeeData = await employeeResponse.json();
      if (!employeeData || !employeeData.employee || !employeeData.employee._id) {
        throw new Error('Invalid employee data received');
      }
      
      const employeeId = employeeData.employee._id;
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          employeeId: employeeId
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit checklist');
      }
      
      const data = await response.json();
      setChecklist(data.checklist);
      setSuccess('Checklist submitted successfully!');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error submitting checklist:', err);
      setError(err.message || 'Failed to submit checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveRemarks = async (index) => {
    if (!checklist || !checklist._id || !checklist.items || !checklist.items[index]) {
      console.error('Cannot save remarks: Checklist data is incomplete');
      return;
    }
    
    try {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        console.error('Cannot save remarks: No authentication token');
        return;
      }
      
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/checklists/update-item`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          checklistId: checklist._id,
          itemIndex: index,
          response: checklist.items[index].response,
          remarks: checklist.items[index].remarks
        })
      });
    } catch (err) {
      console.error('Error saving remarks:', err);
    }
  };

  const getFormTitle = () => {
    switch (formType) {
      case '1040':
        return '1040 - Individual Tax Return';
      case '1120S':
        return '1120S - S Corporation Tax Return';
      case '1120':
        return '1120 - C Corporation Tax Return';
      case '1065':
        return '1065 - Partnership Tax Return';
      case 'Bookkeeping':
        return 'Bookkeeping';
      default:
        return formType;
    }
  };

  const getStatusBadge = () => {
    if (!checklist?.status) return null;
    const styles = {
      draft: 'bg-amber-300 text-amber-950',
      submitted: 'bg-blue-400 text-white',
      completed: 'bg-green-500 text-white',
    };
    const labels = { draft: 'Draft', submitted: 'Submitted', completed: 'Completed' };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${styles[checklist.status] || 'bg-gray-400 text-white'}`}>
        {labels[checklist.status] || 'Unknown'}
      </span>
    );
  };

  const getOptionStyles = (option, response = '', isCommentOpen = false) => {
    const responseMap = { yes: 'Yes', no: 'No', na: 'NA' };
    const isSelected = option === 'comment'
      ? isCommentOpen
      : response === responseMap[option];

    if (!isSelected) {
      return 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700';
    }

    switch (option) {
      case 'yes':
        return 'bg-green-500 text-white shadow-lg shadow-green-500/30';
      case 'no':
        return 'bg-red-500 text-white shadow-lg shadow-red-500/30';
      case 'na':
        return 'bg-gray-500 text-white shadow-lg shadow-gray-500/30';
      case 'comment':
        return 'bg-blue-500 text-white shadow-lg shadow-blue-500/30';
      default:
        return 'bg-gray-100 dark:bg-neutral-800';
    }
  };

  const toggleComment = (index) => {
    setCommentOpen((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const calculateProgress = () => {
    if (!checklist?.items) return 0;
    return checklist.items.filter((item) => item.response).length;
  };

  const totalQuestions = checklist?.items?.length || 0;
  const isDraft = checklist?.status === 'draft';

  return renderModalShell(
    <>
      <div className="p-5 bg-gradient-to-r from-primary-600 to-primary-700 text-white shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <FaClipboardCheck className="w-5 h-5 shrink-0" />
              <h2 className="text-lg font-bold">{getFormTitle()} Checklist</h2>
              {checklist && getStatusBadge()}
            </div>
            <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 bg-white/20 rounded-lg text-xs font-semibold uppercase tracking-wide">
              <FaUser className="w-3 h-3" />
              {resolvedMemberIndex === -1 ? 'Primary Client' : `Member ${resolvedMemberIndex + 1}`}
            </span>
          </div>
          <button type="button" onClick={onHide} className="p-2 hover:bg-white/15 rounded-xl transition-colors shrink-0" aria-label="Close">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="px-6 sm:px-8 py-6 sm:py-7 overflow-y-auto flex-1 bg-white dark:bg-neutral-900">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
            <h5 className="mt-4 font-semibold text-gray-900 dark:text-white">Loading Checklist</h5>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please wait while we prepare your checklist items...</p>
          </div>
        ) : error ? (
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl">
            <FaExclamationTriangle className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold mb-1">Error Loading Checklist</h5>
              <p className="text-sm mb-0">{error}</p>
            </div>
          </div>
        ) : success ? (
          <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-xl">
            <FaCheckCircle className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold mb-1">Success!</h5>
              <p className="text-sm mb-0">{success}</p>
            </div>
          </div>
        ) : checklist ? (
          <>
            <div className="mb-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50 rounded-xl">
              <p className="text-sm text-blue-800 dark:text-blue-300 leading-normal">
                Review each item and select <strong>YES</strong>, <strong>NO</strong>, or <strong>NA</strong>.
                Use <strong>COMMENT</strong> to add optional remarks on any item.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden shadow-sm">
              <div className="flex items-center justify-between p-4 bg-white dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-blue-500/30">
                    P
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-white text-base">
                    Preparer Checklist
                  </span>
                </div>
                <span className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-neutral-700 text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-neutral-600">
                  {calculateProgress()}/{totalQuestions}
                </span>
              </div>

              <div className="p-4 space-y-3 bg-gray-50 dark:bg-neutral-900/50">
                {checklist.items.map((item, index) => (
                  <div
                    key={index}
                    className="p-4 rounded-xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 shadow-sm"
                  >
                    <p className="text-gray-900 dark:text-gray-200 leading-relaxed mb-3 text-sm">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold text-xs mr-2 align-top">
                        {index + 1}
                      </span>
                      {item.text}
                    </p>

                    <div className="flex gap-1.5">
                      {[
                        { key: 'yes', value: 'Yes', label: 'YES' },
                        { key: 'no', value: 'No', label: 'NO' },
                        { key: 'na', value: 'NA', label: 'NA' },
                        { key: 'comment', value: null, label: 'COMMENT' },
                      ].map(({ key, value, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            if (key === 'comment') {
                              toggleComment(index);
                            } else {
                              handleResponseChange(index, value);
                            }
                          }}
                          disabled={!isDraft && key !== 'comment'}
                          className={`flex-1 md:flex-none md:w-24 px-1 py-2 md:py-1.5 rounded-lg font-bold text-xs transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] md:min-h-0 ${getOptionStyles(key, item.response, commentOpen[index])}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {commentOpen[index] && (
                      <textarea
                        rows={2}
                        value={item.remarks || ''}
                        onChange={(e) => handleRemarksChange(index, e.target.value)}
                        onBlur={() => handleSaveRemarks(index)}
                        placeholder="Add a comment..."
                        disabled={!isDraft}
                        className="mt-4 w-full p-3 rounded-xl bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none text-sm disabled:opacity-60"
                      />
                    )}

                    {!isDraft && item.remarks && !commentOpen[index] && (
                      <div className="mt-3 p-3 rounded-xl bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 text-sm text-gray-700 dark:text-gray-300">
                        {item.remarks}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {isDraft ? (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-6 pt-5 border-t border-gray-200 dark:border-neutral-700">
                <button
                  type="button"
                  onClick={onHide}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded-lg transition-colors min-h-[44px]"
                >
                  <FaTimes className="w-3.5 h-3.5" />
                  Save &amp; Close
                </button>
                <button
                  type="button"
                  onClick={handleSubmitChecklist}
                  disabled={submitting || !checklist.items.every((item) => item.response)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-md shadow-primary-600/20 min-h-[44px]"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="w-4 h-4" />
                      Submit Checklist
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-6 p-4 bg-gray-50 dark:bg-neutral-800 rounded-xl text-sm text-gray-600 dark:text-gray-400">
                <FaInfoCircle className="w-4 h-4 shrink-0 text-primary-500" />
                <span>This checklist has been <strong className="text-gray-900 dark:text-white">{checklist.status}</strong>. It is now read-only.</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded-xl text-sm">
            <FaExclamationTriangle className="w-5 h-5 shrink-0" />
            <span>No checklist data available for this client.</span>
          </div>
        )}
      </div>
    </>
  );
};

export default ChecklistModal;
